import axios from 'axios';
import * as _ from 'lodash';
import { ROOT_PARENT_ORG_NUMBER } from 'src/consts';
import { EOrganizationStatus, EOrgType } from 'src/enums';
import { IOrganization, IResponse } from 'src/models';
import ssoService  from './sso.service';

function assignOrganizationsLevelAsync(
    organizations: IOrganization[],
    group: { [key: string]: IOrganization[] }
): IOrganization[] {
    organizations = _.cloneDeep(organizations);
    const newOrganizations: IOrganization[] = [];
    const rootOrgs = _.uniqBy(
        _.concat(group[ROOT_PARENT_ORG_NUMBER] || [], group['null'] || [], group[''] || []),
        'org_number'
    );
    function assignLevel(groupItems: IOrganization[], level: number) {
        if (!groupItems?.length) {
            return;
        }
        _.forEach(groupItems, (item) => {
            item._level = level;
            newOrganizations.push(item);
            assignLevel(group[item.org_number], level + 1);
        });
    }
    assignLevel(rootOrgs, 0);
    return _.uniqBy(newOrganizations, 'org_number');
}

function assignOrganizationsHasChildrenAsync(
    organizations: IOrganization[],
    group: { [key: string]: IOrganization[] }
): IOrganization[] {
    organizations = _.cloneDeep(organizations);
    _.forEach(organizations, (organization) => {
        organization.has_children = !!group[organization.org_number]?.length;
    });
    return organizations;
}

function assignOrganizationsPropertiesAsync(organizations: IOrganization[]): IOrganization[] {
    organizations = _.cloneDeep(organizations);
    const group = _.groupBy(organizations, 'parent_org_number');
    organizations = assignOrganizationsLevelAsync(organizations, group);
    organizations = assignOrganizationsHasChildrenAsync(organizations, group);
    // 添加交互属性
    _.forEach(organizations, (item) => {
        item._is_expand = true;
        item._is_show = true;
        item._has_loaded_children = true;
    });
    return organizations;
}

function filterOrganizations(organizations: IOrganization[], q: string): IOrganization[] {
    organizations = _.cloneDeep(organizations);
    q = q && q.trim();
    if (!q) {
        return organizations;
    }
    const leafOrgs = _.filter(organizations, (organization) =>
        _.includes(organization.org_name, q) || _.includes(organization.org_number, q)
    );
    let newOrganizations: IOrganization[] = [...leafOrgs];

    function filterOrgs(orgs: IOrganization[]) {
        if (!orgs.length) {
            return;
        }
        let parentOrgs: IOrganization[] = [];
        _.forEach(orgs, (org) => {
            const parent = _.find(organizations, { org_number: org.parent_org_number });
            if (parent) {
                parentOrgs.push(parent);
            }
        });
        parentOrgs = _.uniqBy(parentOrgs, 'org_number');
        newOrganizations = _.concat(newOrganizations, parentOrgs);
        filterOrgs(parentOrgs);
    }
    filterOrgs(leafOrgs);

    return _.uniqBy(newOrganizations, 'org_number');
}

function sortOrganizations(organizations: IOrganization[]): IOrganization[] {
    // 组织先按type排序，type相同时，按组织名称拼音顺序排序
    return organizations.sort((param1, param2) => {
        if (param1.type === param2.type) {
            return param1['org_name'].localeCompare(param2['org_name'], 'zh');
        } else {
            return param1.type - param2.type;
        }
    });
}

export default {
    // 获取组织列表
    getOrganizations(q?: string, include_disabled = false): Promise<IOrganization[]> {
        const params = { page: 1, limit: 999, include_disabled };
        return axios.get<IOrganization[]>(`/api/organizations`, { params }).then((res) => {
            let organizations = filterOrganizations(res.data, q);
            organizations = sortOrganizations(organizations);
            return assignOrganizationsPropertiesAsync(organizations);
        });
    },

    // 获取组织总数
    getOrganizationsCount(include_disabled = false): Promise<number> {
        const params = { include_disabled };
        return axios.get<number>(`/api/organizations-count`, { params }).then((res) => {
            return res.data;
        });
    },

    // 获取子组织列表
    getChildrenOrganizations(orgNumber: string = ROOT_PARENT_ORG_NUMBER): Promise<IOrganization[]> {
        return axios
            .get<IOrganization[]>(`/api/organizations/${orgNumber}/children`)
            .then((res) => {
                const organizations = sortOrganizations(res.data);
                _.forEach(organizations, (item) => {
                    item._is_show = true;
                });
                return organizations;
            });
    },

    // 获取组织详情
    getOrganization(orgNumber: string): Promise<IOrganization> {
        return axios.get<IOrganization>(`/api/organizations/${orgNumber}`).then((res) => {
            return res.data;
        });
    },

    // 获取最近使用组织列表
    getLatelyOrganizations(): Promise<IOrganization[]> {
        return axios.get<IOrganization[]>('/api/lately/organizations').then((res) => {
            return res.data;
        });
    },

    // 添加最近使用组织
    saveLatelyOrganization(orgNumber: string): Promise<IResponse> {
        return axios
            .post<IResponse>('/api/lately/organizations', { org_number: orgNumber })
            .then((res) => {
                return res.data;
            });
    },

    // 创建组织
    createOrganization(organization: IOrganization): Promise<IResponse> {
        return axios.post<IResponse>(`/api/organizations`, organization).then((res) => {
            return res.data;
        });
    },

    // 更新组织
    updateOrganization(organization: IOrganization): Promise<IResponse> {
        return axios.put<IResponse>(`/api/organizations`, organization).then((res) => {
            return res.data;
        });
    },

    // 启用/停用 组织
    toggleEnableOrganization(orgNumber: string): Promise<IResponse> {
        return this.getOrganization(orgNumber)
            .then((organization) => {
                organization.org_status =
                    EOrganizationStatus.Disabled === organization.org_status
                        ? EOrganizationStatus.Enable
                        : EOrganizationStatus.Disabled;
                return axios.put<IResponse>(`/api/organizations`, organization);
            })
            .then((res) => {
                return res.data;
            });
    },

    // 获取所有子组织部门，包括子级的子级
    getAllChildrenOrganizations(orgNumber: string): Promise<IOrganization[]> {
        return this.getOrganizations('', true).then((organizations) => {
            return this.getChildrenOrgs(orgNumber, organizations);
        });
    },

    getChildrenOrgs(orgNumber: string, organizations: IOrganization[]): IOrganization[] {
        const item = _.find(organizations, { org_number: orgNumber });
        if (item.has_children) {
            let children = _.filter(organizations, { parent_org_number: orgNumber });
            _.forEach(children, (child) => {
                children = _.concat(children, this.getChildrenOrgs(child.org_number, organizations));
            });
            return children;
        }
        return [];
    },

    // 区分组织架构和项目组
    insertOrgTypeNameIntoOrganizations(items: IOrganization[]): IOrganization[] {
        if (!ssoService.getIsSso()) {
            return items;
        }
        if(!items.length) {
            return [];
        }
        const organization = { 
            org_name: '组织架构', 
            type: EOrgType.Default,
            _is_fake_org: true, 
            _is_show: true,
        } as unknown as IOrganization;
        const ProjectOrg = {
            org_name: '项目组', 
            type: EOrgType.ProjectOrg,
            _is_fake_org: true, 
            _is_show: true 
        } as unknown as IOrganization;
        const index = _.findIndex(items, { type: EOrgType.Default });
        if (-1 !== index) {
            items.unshift(organization);
        }
        const firstProjectOrgIndex = _.findIndex(items, { type: EOrgType.ProjectOrg });
        if (-1 === firstProjectOrgIndex) {
            return items;
        }
        items = [
            ...items.slice(0, firstProjectOrgIndex),
            ...[ProjectOrg],
            ...items.slice(firstProjectOrgIndex),
        ];
        return items;
    }
};
